import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from "@ngrx/operators";
import { of, pipe, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { PhysicalAssessmentsService } from '@app-physical-assessments/data-access/infra/physical-assessments.service';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@shared/stores/prop-state.store';
import { AuthStore } from '@app-auth/data-access/auth.store';
import { IAerobicCapacityEstimatedProtocol, IAerobicCapacityRealizedProtocol, IAnamnesis, IBodyComposition, IBodyCompositionProtocol, IFlexibility, IPerson, IPhysicalAssessment, IPhysicalAssessments, IRating, IRatingResult } from '@app-physical-assessments/data-access/entities/physical-assessments.interface';
import { sortByDateDescendingOrder } from '@shared/utils/sort-by-date.util';
import { EPhysicalActivityLevel, EPhysicalActivityLevelLabel, EPhysicalActivityLevelValueFemale, EPhysicalActivityLevelValueMale, ESex } from '@app-physical-assessments/data-access/entities/physical-assessments.enum';
import { AlertStore } from '@shared/components/alert/data-access/alert.store';
import { EAlertModes, EAlertTypes } from '@shared/components/alert/data-access/entities/alert.enum';
import { Router } from '@angular/router';

interface IPhysicalAssessmentsState {
    PhysicalAssessments: IPhysicalAssessments | null;
    PhysicalAssessment: IPhysicalAssessment | null;
    Person: IPerson | null;
    BodyCompositionProtocolById: Record<number, IBodyCompositionProtocol>;
    AerobicCapacityRealizedProtocolById: Record<number, IAerobicCapacityRealizedProtocol>;
    AerobicCapacityEstimatedProtocolById: Record<number, IAerobicCapacityEstimatedProtocol>;
};

const initialState: IPhysicalAssessmentsState = {
    PhysicalAssessments: null,
    PhysicalAssessment: null,
    Person: null,
    BodyCompositionProtocolById: {},
    AerobicCapacityRealizedProtocolById: {},
    AerobicCapacityEstimatedProtocolById: {
        1: {
            EstimatedProtocolId: 1,
            ShortDescription: 'Jackson, A.s. e col. (1990)',
        },
        2: {
            EstimatedProtocolId: 2,
            ShortDescription: 'Mathews et al. (1999/EUA)',
        },
    },
};

export const PhysicalAssessmentsStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((
        store,
        physicalAssessmentsService = inject(PhysicalAssessmentsService),
        authStore = inject(AuthStore),
        alertStore = inject(AlertStore),
        router = inject(Router),
    ) => {

        const updateAnamnesis = (anamnesis: IAnamnesis, person: IPerson) => {

            const objectives = []
            if (anamnesis.AimForCompetition) {
                objectives.push('Competição');
            }
            if (anamnesis.AimForConditioning) {
                objectives.push('Condicionamento');
            }
            if (anamnesis.AimForDefinition) {
                objectives.push('Definição');
            }
            if (anamnesis.AimToLoseWeight) {
                objectives.push('Emagrecer');
            }
            if (anamnesis.AimForHypertrophy) {
                objectives.push('Hipertrofia');
            }
            if (anamnesis.AimForRecreation) {
                objectives.push('Lazer');
            }
            if (anamnesis.AimForHealth) {
                objectives.push('Saúde');
            }
            if (anamnesis.TherapeuticAim) {
                objectives.push('Terapêutico');
            }
            if (anamnesis.AimOtherText) {
                objectives.push(anamnesis.AimOtherText);
            }

            anamnesis.Objectives = objectives.join(', ');

            anamnesis.PainsInjuriesDescription = anamnesis.PainsInjuries?.map(p => p.Description).join(', ') || '';

            if (anamnesis.ParQ1 || anamnesis.ParQ2 ||
                anamnesis.ParQ3 || anamnesis.ParQ4 ||
                anamnesis.ParQ5 || anamnesis.ParQ6 ||
                anamnesis.ParQ7) {
                anamnesis.ParQ = true;
            } else {
                anamnesis.ParQ = false;
            }

            const key = EPhysicalActivityLevel[anamnesis.PhysicalActivityLevel] as keyof typeof EPhysicalActivityLevelLabel;
            anamnesis.PhysicalActivityLevelDescription = EPhysicalActivityLevelLabel[key];
            if (person.Gender === ESex.MALE || person.Gender === ESex.OTHER) {
                anamnesis.PhysicalActivityLevelValue = EPhysicalActivityLevelValueMale[key];
                return;
            }
            if (person.Gender === ESex.FEMALE) {
                anamnesis.PhysicalActivityLevelValue = EPhysicalActivityLevelValueFemale[key];
                return;
            }
        }

        const updateBodyComposition = (bodyComposition: IBodyComposition, person: IPerson, anamnesis?: IAnamnesis) => {
            if (bodyComposition.WeightKg && bodyComposition.HeightCm) {
                bodyComposition.BodyMassIndex = bodyComposition.WeightKg / Math.pow((bodyComposition.HeightCm / 100), 2);
                if (person.Gender === ESex.MALE || person.Gender === ESex.OTHER) {
                    bodyComposition.BasalMetabolicRate = 66 + (13.7 * bodyComposition.WeightKg) + (5 * bodyComposition.HeightCm) - (6.8 * person.Age)
                }
                if (person.Gender === ESex.FEMALE) {
                    bodyComposition.BasalMetabolicRate = 655 + (9.6 * bodyComposition.WeightKg) + (1.7 * bodyComposition.HeightCm) - (4.7 * person.Age)
                }

                if (anamnesis?.PhysicalActivityLevelValue) {
                    bodyComposition.TotalEnergyExpenditure = bodyComposition.BasalMetabolicRate * anamnesis.PhysicalActivityLevelValue;
                }
            }
        }

        const updateFlexibility = (flexibility: IFlexibility) => {
            flexibility.FlexibilityResult = Object.entries(flexibility).reduce((acc, [key, value]) => {
                if (key.includes('FlexitestMove')) return acc + value;
                return acc;
            }, 0);
        }

        const getRating = (age: number, result: number, table: any[]): IRatingResult => {
            const rate: IRating = getRatingGroupByAge(age, table);

            const { Idade, ...rating } = rate;
            return getRatingByResult(result, rating);
        }

        const getRatingByResult = (result: number, rating: Record<string, string>): IRatingResult => {
            const key = Object.keys(rating).find((key) => {
                const range = rating[key];

                if (range.includes('-')) {
                    const [start, end] = range.split('-').map(Number);
                    return result >= start && result <= end;
                }

                if (range.includes('<=')) {
                    return result <= Number(range.split('<=')[1]);
                }

                if (range.includes('>=')) {
                    return result >= Number(range.split('>=')[1]);
                }

                return false;
            }) as string;

            return { rating: key, range: rating[key] };
        }

        const getRatingGroupByAge = (age: number, table: any[]): any => {
            const matchingRange = table?.find((item) => {
                const [rangeInitialAge, rangeFinalAge] = item?.Idade?.includes('-') ? item?.Idade?.split('-').map(Number) : [0, 0];
                const rangeStartInclusive = item?.Idade?.includes('+') ? Number(item?.Idade?.split('+')[1]) : rangeInitialAge;

                return (rangeFinalAge ? age > rangeInitialAge && age < rangeFinalAge || age === rangeInitialAge || age === rangeFinalAge : age >= rangeStartInclusive);
            });

            return matchingRange;
        }
        return {
            getRatingGroupByAge,
            getRatingByResult,
            getRating,

            getPhysicalAssessments: rxMethod<void>(pipe(
                tap(() => patchState(store, { ...setPropLoading('PhysicalAssessments') })),
                switchMap(() => {
                    const personId = authStore.AuthData()?.PersonId;
                    if (!personId) return of(null);

                    return physicalAssessmentsService.getPhysicalAssessments(personId).pipe(
                        tapResponse({
                            next: (response) => {
                                response.SummaryAssessments = sortByDateDescendingOrder(response.SummaryAssessments, ['AddedOn']);
                                patchState(store, { PhysicalAssessments: response, ...setPropLoaded('PhysicalAssessments') });
                            },
                            error: (error: HttpErrorResponse) => {
                                patchState(store, { ...setPropError('PhysicalAssessments') });
                            }
                        })
                    )
                })
            )),

            getPhysicalAssessmentById: rxMethod<string>(pipe(
                tap(() => patchState(store, { ...setPropLoading('PhysicalAssessment') })),
                switchMap((assessmentId) => {
                    return physicalAssessmentsService.getPhysicalAssessmentById(assessmentId).pipe(
                        tapResponse({
                            next: ({ AssessmentsGrouped, Person }) => {
                                const PhysicalAssessment = AssessmentsGrouped.find((assessment) => assessment?.Assessment?.AssessmentId === assessmentId);
                                if (!PhysicalAssessment) {
                                    alertStore.openAlert({
                                        title: 'Avaliação não encontrada.',
                                        message: 'Não foi possível carregar essa avaliação.',
                                        type: EAlertTypes.WARNING, mode: EAlertModes.ALERT
                                    });
                                    patchState(store, { Person: null, PhysicalAssessment: null, ...setPropLoaded('PhysicalAssessment') });
                                    router.navigate(['/afig']);
                                    return;
                                }
                                if (PhysicalAssessment.Anamnesis) updateAnamnesis(PhysicalAssessment.Anamnesis, Person);
                                if (PhysicalAssessment.BodyComposition) updateBodyComposition(PhysicalAssessment.BodyComposition, Person, PhysicalAssessment.Anamnesis);
                                if (PhysicalAssessment.Flexibility) updateFlexibility(PhysicalAssessment.Flexibility)
                                patchState(store, { Person, PhysicalAssessment, ...setPropLoaded('PhysicalAssessment') });
                            },
                            error: (error: HttpErrorResponse) => {
                                alertStore.openAlert({
                                    title: 'Ops!',
                                    message: 'Não foi possível carregar essa avaliação.',
                                    type: EAlertTypes.WARNING,
                                    mode: EAlertModes.ALERT
                                });
                                router.navigate(['/afig']);
                                patchState(store, { ...setPropError('PhysicalAssessment') });
                            }
                        })
                    )
                })
            )),

            getAerobicCapacityRealizedProtocolById: rxMethod<number>(
                switchMap((id: number) => {
                    return physicalAssessmentsService.getAerobicCapacityRealizedProtocolById(id).pipe(
                        tapResponse({
                            next: (response) => {
                                const aerobicCapacityRealizedProtocol = response.AerobicCapacityRealizedProtocols[0];
                                if (aerobicCapacityRealizedProtocol?.Description) aerobicCapacityRealizedProtocol.ShortDescription = aerobicCapacityRealizedProtocol.Description.split('.')[1];
                                patchState(store, { AerobicCapacityRealizedProtocolById: { ...store.AerobicCapacityRealizedProtocolById(), [id]: aerobicCapacityRealizedProtocol } });
                            },
                            error: (error: HttpErrorResponse) => {
                                console.log(error);
                            }
                        })
                    )
                })
            ),

            getBodyCompositionProtocolById: rxMethod<number>(
                switchMap((id) => {
                    return physicalAssessmentsService.getBodyCompositionProtocolById(id).pipe(
                        tapResponse({
                            next: (response) => {
                                const bodyCompositionProtocol = response.BodyCompositionProtocols[0];
                                if (bodyCompositionProtocol) {
                                    const splitProtocol = bodyCompositionProtocol.Description.split(',');
                                    const firstPart = splitProtocol.shift();
                                    bodyCompositionProtocol.ShortDescription = firstPart?.split('.')[1];
                                    bodyCompositionProtocol.LognDescription = splitProtocol.join(',');
                                }
                                patchState(store, { BodyCompositionProtocolById: { ...store.BodyCompositionProtocolById(), [id]: bodyCompositionProtocol } });
                            },
                            error: (error: HttpErrorResponse) => {
                                console.error(error);
                            }
                        })
                    )
                }
                )),

            resetStore(): void {
                patchState(store, initialState, setPropInit('PhysicalAssessments', 'PhysicalAssessment'));
            }
        };
    }),
    withReqState('PhysicalAssessments', 'PhysicalAssessment'),

);